// Mesh unicast address math, mirroring the App's logic.
//
// Each PCB owns a contiguous block of mesh unicast addresses starting at
// pcb.unicastAddress. Within that block, the first `loads` addresses are
// the load endpoints (appliances), and the next `controls` addresses are
// the UI inputs (manual controls / regulators).
//
// Backend layout per module type (per user spec):
//   2Sw       — 2 loads + 2 controls (P, P+1 = relays; P+2, P+3 = switches)
//   Knob      — 1 load + 1 control  (P = load; P+1 = regulator)
//   KnobPro   — same as Knob
//   3R1F / 4R — 4 loads, 0 controls (controller series, all appliances)
//   Plug      — 1 load, 0 controls
//
// Inputs we have without any backend change:
//   pcb.unicastAddress   (already in GET_USERS_DETAIL_URL response)
//   pcb.type             (already in response)
//   switch.no            (already in response — 1-indexed position)
//   switch.pcbId         (links a switch to its parent PCB)

export interface PcbLayout {
    loads: number
    controls: number
}

export const PCB_LAYOUT: Record<string, PcbLayout> = {
    '0':  { loads: 2, controls: 2 }, // 2Sw
    '2':  { loads: 1, controls: 1 }, // Knob
    '3':  { loads: 1, controls: 1 }, // KnobPro
    '4':  { loads: 4, controls: 0 }, // 3R1F
    '5':  { loads: 4, controls: 0 }, // 4R
    '98': { loads: 1, controls: 0 }, // Universal Plug
    '99': { loads: 1, controls: 0 }, // Indian Plug
}

const layoutFor = (pcb: any): PcbLayout | null => {
    if (!pcb) return null
    return PCB_LAYOUT[String(pcb.type)] || null
}

const primaryFor = (pcb: any): number | null => {
    if (!pcb) return null
    if (pcb.unicastAddress == null || pcb.unicastAddress === -1) return null
    return pcb.unicastAddress
}

// Address of the LOAD endpoint controlled by this switch (= the appliance).
// Used by the Appliances view.
export function loadAddressForSwitch(sw: any, pcb: any): number | null {
    const base = primaryFor(pcb)
    if (base == null) return null
    const n = sw?.no
    if (n == null || n < 1) return null
    return base + (n - 1)
}

// Address of the UI INPUT element of this switch (= manual control).
// Used by the Manual Controls view. Returns null for controller-series
// PCBs (no controls) or when the layout is unknown.
export function controlAddressForSwitch(sw: any, pcb: any): number | null {
    const base = primaryFor(pcb)
    const layout = layoutFor(pcb)
    if (base == null || !layout || layout.controls === 0) return null
    const n = sw?.no
    if (n == null || n < 1) return null
    return base + layout.loads + (n - 1)
}
